import type { AggregatedQto } from "../../types";
import type { AtoTakeoffItemRaw, AtoTypeInfo } from "./types";

function num(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim()) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function itemQuantity(item: AtoTakeoffItemRaw): number {
  return (
    num(item.quantity) ??
    num(item.totalQuantity) ??
    num(item.total_quantity) ??
    num(item.primaryQuantity?.quantity) ??
    num(item.measurement?.value) ??
    0
  );
}

function itemUom(item: AtoTakeoffItemRaw): string {
  const raw =
    [item.unit, item.uom, item.primaryQuantity?.unitOfMeasure, item.measurement?.unit].find(
      (s) => typeof s === "string" && s.trim()
    ) || "";
  return String(raw).trim() || "ea";
}

interface TypeBucket {
  typeId: string;
  typeName: string;
  ediphiProductId: string | null;
  quantity: number;
  uom: string;
  customId: string | null;
}

/**
 * Aggregate takeoff items by their takeoff type, producing one
 * AggregatedQto per type. Quantities are summed across all items
 * that share the same type. ACC's API contract guarantees every
 * item has an `id` and `takeoffTypeId`, so we trust those fields
 * directly — see `AtoTakeoffItemRaw` for the typed contract.
 *
 * UOM is sourced from the takeoff type (`primaryQuantityDefinition.unitOfMeasure`)
 * rather than per-item, because all items of a given type produce quantities
 * in that single type-defined UOM. The per-item `itemUom` is kept only as a
 * defensive fallback for the rare case where the types fetch failed but items
 * succeeded (see `loadAtoAggregatedForPackage`, which doesn't short-circuit).
 */
export function mapTakeoffItemsToAggregated(
  items: AtoTakeoffItemRaw[],
  typeInfoById?: Map<string, AtoTypeInfo>
): AggregatedQto[] {
  const buckets = new Map<string, TypeBucket>();

  for (const item of items) {
    const typeId = item.takeoffTypeId;
    let bucket = buckets.get(typeId);
    if (!bucket) {
      const info = typeInfoById?.get(typeId);
      const orderRaw = item.order ?? item.classificationCustomId;
      bucket = {
        typeId,
        typeName: info?.name ?? "",
        ediphiProductId: info?.ediphiProductId ?? null,
        quantity: 0,
        uom: info?.unitOfMeasure ?? itemUom(item),
        customId: orderRaw == null ? null : String(orderRaw).trim() === "" ? null : String(orderRaw),
      };
      buckets.set(typeId, bucket);
    }

    bucket.quantity += itemQuantity(item);
  }

  const out: AggregatedQto[] = [];
  buckets.forEach((bucket) => {
    out.push({
      qtoId: bucket.typeId,
      qtoName: bucket.typeName || `Takeoff type ${bucket.typeId}`,
      qtoType: bucket.typeName || bucket.typeId,
      quantity: bucket.quantity,
      uom: bucket.uom,
      qtoCustomId: bucket.customId,
      ediphiProductId: bucket.ediphiProductId,
    });
  });

  return out;
}
